import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Heart from "../../../../Assets/Images/HEART.png";
import Soul from "../../../../Assets/Images/SOUL.png";
import Mind from "../../../../Assets/Images/MIND.png";
import Strength from "../../../../Assets/Images/STRENGTH.png";
import HeaderPreview from '../../../GeneratePdf/HeaderPreview';
import FooterPreview from '../../../GeneratePdf/FooterPreview';
import GroupReportContext from '../../../../Context/GroupReportContext';
import { useGroupReportDate } from '../../../../Context/GroupReportDate';
import { logItemsOverviewData } from '../../../../utils/itemsOverviewLogger';

const initialData = [
  { quadrant: 'HEART', no: 1, question: 'When presented with a problem, [First Name] is most concerned with how the team is handling it.', score: 1 },
  { quadrant: 'SOUL', no: 1, question: "[First Name]'s superpower is to rise above any situation to gain a wise perspective.", score: 2 },
  { quadrant: 'MIND', no: 1, question: 'When presented with a problem, [First Name] needs to understand the processes before starting.', score: 3 },
  { quadrant: 'STRENGTH', no: 1, question: 'When presented with a problem, [First Name] immediately starts working on it.', score: 4 },
  { quadrant: 'SOUL', no: 2, question: '[First Name] evaluates decisions by their ability to fulfill the greater vision.', score: 2 },
  { quadrant: 'MIND', no: 2, question: '[First Name] makes decisions based on the policies that guide them.', score: 3 },
  { quadrant: 'STRENGTH', no: 2, question: '[First Name] makes decisions by focusing on what needs to get done.', score: 4 },
  { quadrant: 'HEART', no: 2, question: '[First Name] ensures their decisions are measured by whether everyone is on board.', score: 1 },
  { quadrant: 'STRENGTH', no: 3, question: '[First Name] feels most fulfilled when personal tasks are completed.', score: 4 },
  { quadrant: 'HEART', no: 3, question: '[First Name] feels most fulfilled when surrounded by their people.', score: 1 },
  { quadrant: 'SOUL', no: 3, question: '[First Name] is most fulfilled when living out a greater purpose.', score: 2 },
  { quadrant: 'MIND', no: 3, question: '[First Name] is most fulfilled when a project works like it was planned.', score: 3 },
];

const sideLabels = [
  {
    "label": "PROBLEM APPROACH",
    "bg": "#394554"
  },
  {
    "label": "DECISION MAKING",
    "bg": "#666666"
  },
  {
    "label": "FULFILLMENT",
    "bg": "#394554"
  },
  {
    "label": "DRIVERS",
    "bg": "#666666"
  },
  {
    "label": "GO-TO'S",
    "bg": "#394554"
  },
  {
    "label": "GUIDING PRINCIPLES",
    "bg": "#666666"
  },
  {
    "label": "MEANING",
    "bg": "#394554"
  },
  {
    "label": "PATTERNS",
    "bg": "#666666"
  },
  {
    "label": "ORIENTATION",
    "bg": "#394554"
  },
  {
    "label": "REWARDS",
    "bg": "#666666"
  }
]

const Logo = (quadrant) => {
  switch (quadrant) {
    case 'HEART':
      return <img src={Heart} alt="Heart" className="logo" />;
    case 'SOUL':
      return <img src={Soul} alt="Soul" className="logo" />;
    case 'MIND':
      return <img src={Mind} alt="Mind" className="logo" />;
    case 'STRENGTH':
      return <img src={Strength} alt="Strength" className="logo" />;
    default:
      return null;
  }
};

const truncateText = (text, limit) => {
  if (text.length <= limit) return text;
  return text.substring(0, text.lastIndexOf(" ", limit)) + "...";
};

const QF_ItemOnePage360 = ({ apiData, pageNumber, reportType }) => {
  const respondentName = apiData?.fullName?.split(' ')[0] || '[First Name]';
  const [dynamicData, setDynamicData] = useState([...initialData]);
  const groupReportCtx = useContext(GroupReportContext)
  const { updateTablevaluesq2Page1 } = useGroupReportDate();

  const allMembersDataArray = Object.values(apiData?.memberResults?.allMembersData || {});
  const allVariableSets = allMembersDataArray.map(el => el.variables);
  const allAssessmentDesignation = allMembersDataArray.map(el => el?.designation);
  const allAssessmentHash = allMembersDataArray.map(el => el?.hash);
  const allAssessmentFullName = allMembersDataArray.map(el => el?.fullName);

  // console.log("apiData",apiData,allMembersDataArray)

  useEffect(() => {
    if (!apiData) {
      setDynamicData(initialData);
      return;
    }

    // 1. Extract Self Data from apiData.bottomQuestions
    const selfScoresMap = new Map();
    if (apiData.bottomQuestions && Array.isArray(apiData.bottomQuestions) && apiData.bottomQuestions.length > 0) {
      apiData.bottomQuestions.forEach(item => {
        const match = item.variable?.match(/^st(\d+)_self_pass([12])$/);
        if (!match) return;

        const rawNo = parseInt(match[1]);
        const passType = match[2];

        let actualScore;
        let actualQuestionNo;

        if (rawNo >= 1 && rawNo <= 10) {
          actualScore = passType === "1" ? 4 : 1;
          actualQuestionNo = rawNo;
        } else if (rawNo >= 11 && rawNo <= 20) {
          actualScore = passType === "1" ? 3 : 2;
          actualQuestionNo = rawNo - 10;
        } else {
          return;
        }

        const key = `${actualQuestionNo}_${actualScore}`;
        selfScoresMap.set(key, actualScore);
      });
    }

    // 2. Group Observers by Designation
    const observersByDesignation = {
      Manager: [],
      DirectReport: [],
      Peer: [],
      Other: []
    };

    if (apiData.memberResults && apiData.memberResults.allMembersData) {
      // Handle allMembersData as an object with keys
      const allMembersData = apiData.memberResults.allMembersData;

      // Convert object to array of members
      const membersArray = Object.values(allMembersData);

      membersArray.forEach(member => {
        const designation = member.designation;

        if (designation && designation.includes('Manager')) {
          observersByDesignation.Manager.push(member);
        } else if (designation && designation.includes('Direct Report')) {
          observersByDesignation.DirectReport.push(member);
        } else if (designation && designation.includes('Peer')) {
          observersByDesignation.Peer.push(member);
        } else if (designation && designation.includes('Other')) {
          observersByDesignation.Other.push(member);
        }
      });
    }

    // 3. Extract Observer Scores
    const observerScoresMap = {
      Manager: new Map(),
      DirectReport: new Map(),
      Peer: new Map(),
      Other: new Map()
    };

    Object.keys(observersByDesignation).forEach(designation => {
      observersByDesignation[designation].forEach(member => {
        if (member.bottomQuestions && Array.isArray(member.bottomQuestions) && member.bottomQuestions.length > 0) {
          member.bottomQuestions.forEach(item => {
            const match = item.variable?.match(/^st(\d+)_self_pass([12])$/);
            if (!match) return;

            const rawNo = parseInt(match[1]);
            const passType = match[2];

            let actualScore;
            let actualQuestionNo;

            if (rawNo >= 1 && rawNo <= 10) {
              actualScore = passType === "1" ? 4 : 1;
              actualQuestionNo = rawNo;
            } else if (rawNo >= 11 && rawNo <= 20) {
              actualScore = passType === "1" ? 3 : 2;
              actualQuestionNo = rawNo - 10;
            } else {
              return;
            }

            const key = `${actualQuestionNo}_${actualScore}`;
            observerScoresMap[designation].set(key, actualScore);
          });
        }
      });
    });

    // 4. Calculate Averages and Update Data
    const updatedData = initialData.map(item => {
      const key = `${item.no}_${item.score}`;

      // Get Self Score
      const selfScore = selfScoresMap.get(key);

      // Get Observer Scores
      const managerScore = observerScoresMap.Manager.get(key);
      const directReportScore = observerScoresMap.DirectReport.get(key);
      const peerScore = observerScoresMap.Peer.get(key);
      const otherScore = observerScoresMap.Other.get(key);

      // Calculate Average
      const allScores = [selfScore, managerScore, directReportScore, peerScore, otherScore]
        .filter(score => score !== undefined && score !== null);

      const average = allScores.length > 0
        ? (allScores.reduce((sum, score) => sum + score, 0) / allScores.length).toFixed(1)
        : '-';

      return {
        ...item,
        self: selfScore || '-',
        manager: managerScore || '-',
        directReport: directReportScore || '-',
        peer: peerScore || '-',
        other: otherScore || '-',
        average: average
      };
    });

    setDynamicData(updatedData);
  }, [apiData]);


  const displayData = dynamicData;

  const quadrantScores = {
    "HEART": 1,
    "SOUL": 2,
    "MIND": 3,
    "STRENGTH": 4
  }

  const allResults = useMemo(() => {
    const results = [];
    displayData?.forEach((el) => {
      const res = groupReportCtx?.calculateQuestionScore(
        quadrantScores?.[el.quadrant],
        allVariableSets,
        el.no,
        "360_overview",
        allAssessmentDesignation,
        allAssessmentHash,
        allAssessmentFullName
      );
      if (res?.averageScore !== null) {
        results.push(res);
      }
    });
    return results;
  }, [displayData, groupReportCtx, allVariableSets, allAssessmentDesignation, allAssessmentHash, allAssessmentFullName]);


  // console.log("allResults,apiData",allResults,apiData)

  // Collect all scores and save to context
  const lastScoresRef = useRef(null);
  const scoresKey = useMemo(() => {
    if (!allResults.length || !dynamicData.length) return null;
    return JSON.stringify({
      results: allResults.slice(0, 12).map(r => r?.averageScore),
      data: dynamicData.slice(0, 12).map(d => d.no)
    });
  }, [allResults, dynamicData]);

  useEffect(() => {
    if (!scoresKey || !groupReportCtx) return;

    const scoresArray = [];
    const first12Items = dynamicData.slice(0, 12);

    first12Items.forEach((item, index) => {
      const selfScore = groupReportCtx?.getAverageScore("360 Assessment", allResults, index);
      const managerScore = groupReportCtx?.getAverageScore("360 Assessment Manager", allResults, index);
      const directReportScore = groupReportCtx?.getAverageScore("360 Assessment Direct Report", allResults, index);
      const peerScore = groupReportCtx?.getAverageScore("360 Assessment Peer", allResults, index);
      const otherScore = groupReportCtx?.getAverageScore("360 Assessment Other", allResults, index);
      const averageScore = groupReportCtx?.getObserverAverageScore(allResults, index) ?? "-";
      scoresArray.push([selfScore, managerScore, directReportScore, peerScore, otherScore, averageScore]);
    });

    const nextStr = JSON.stringify(scoresArray);
    if (lastScoresRef.current !== nextStr) {
      lastScoresRef.current = nextStr;
      updateTablevaluesq2Page1(scoresArray.slice(0, 8));
      logItemsOverviewData({
        pageName: "Quadrant Flow 1",
        items: first12Items.slice(0, 8),
        allResults,
        getAverageScore: groupReportCtx.getAverageScore,
      });
    }
  }, [scoresKey, groupReportCtx]);

  return (
    <div style={{ backgroundColor: "rgb(255, 255, 255)" }}>
      <HeaderPreview />
      <div className="page" style={{ paddingBottom: "0", height: "634px", boxSizing: "border-box", position: "relative" }}>
        <div className="headerBox">
          <h2 className="title">QUADRANT FLOW 360 ITEMS OVERVIEW</h2>
          <p className="subTitle"> This page presents a ranking of all 40 items distributed across the four quadrants: Heart, Soul, Mind, and Strength™. The rankings are based on the OBSERVER ratings (SELF excluded), using a response scale that ranges from 1 (Least) to 4 (Most). Any unselected statement will appear as "-" in the Items Overview chart. </p>
        </div>

        <div className="content" >
          <div className="table" style={{ position: "relative" }}>
            <div className="tableRow tableHeader" style={{ background: "#245775", color: "#fff", display: "flex", zIndex: "1", position: "relative" }}>
              <div className="cellIndex" style={{ marginLeft: "40px", display: "flex", alignItems: "center" }}>#</div>
              <div className="cellQuadrant" style={{ fontSize: "9px", padding: "5.5px 0px 5.5px 13px", lineHeight: "13px" }}>Quadrant</div>
              <div className="cellLogo"></div>
              <div className="cellQuestion" style={{ marginLeft: "4px", marginRight: "10px" }}>Question</div>
              <div className="cellscore-parent" style={{ display: "flex", marginLeft: "16px" }}>
                <div className="cellScore">S</div>
                <div className="cellScore">M</div>
                <div className="cellScore">D</div>
                <div className="cellScore">P</div>
                <div className="cellScore">O</div>
                <div className="cellScore">A</div>
              </div>
            </div>

            {dynamicData.slice(0, 8).map((item, index) => {

              const selfScore = Number(groupReportCtx?.getAverageScore("360 Assessment", allResults, index));
              const managerScore = Number(groupReportCtx?.getAverageScore("360 Assessment Manager", allResults, index));
              const directReportScore = Number(groupReportCtx?.getAverageScore("360 Assessment Direct Report", allResults, index));
              const peerScore = Number(groupReportCtx?.getAverageScore("360 Assessment Peer", allResults, index));
              const otherScore = Number(groupReportCtx?.getAverageScore("360 Assessment Other", allResults, index));
              const averageScore = Number(groupReportCtx?.getObserverAverageScore(allResults, index));
              const getLabelByIndex = (index) => {
                if (index === 0) {
                  return sideLabels[0].label
                }

                return sideLabels[index / 4].label
              }

              const getStyleByIndex = (index) => {
                if (index === 0) {
                  return {
                    top: "12px",
                    height: "50%",
                    backgroundColor: `${sideLabels[0].bg}`,
                  }
                }

                return {
                  top: "53%",
                  height: "50%",
                  backgroundColor: `${sideLabels[index / 4].bg}`
                }
              }

              return <div key={index} style={{ display: "flex", alignItems: "center", padding: "5px 0", }} className={`tableRow ${index % 2 === 0 ? 'evenRow' : 'oddRow'}`}>
                <div className='cellIndex'></div>
                {
                  index === 0 || index % 4 == 0
                    ? <div className="sidelables" style={getStyleByIndex(index)}>{getLabelByIndex(index)}</div>
                    : <></>
                }
                <div className="cellIndex" style={{ marginLeft: "30px" }}>{index + 1}.</div>
                <div className="cellQuadrant" style={{ width: "50px", marginLeft: "11px" }}>{item.quadrant}</div>
                <div className="cellLogo">{Logo(item.quadrant)}</div>
                <div className="cellQuestion">{item.question.replace(/\[First Name\]/g, respondentName)}</div>
                <div className="cellScore-parent" style={{ display: "flex", alignItems: "center", marginLeft: "25px" }}>
                  <div className="cellScore">{typeof selfScore === 'number' && !Number.isNaN(selfScore) ? Number(selfScore).toFixed(1) : '-'}</div>
                  <div className="cellScore">{typeof managerScore === 'number' && !Number.isNaN(managerScore) ? Number(managerScore).toFixed(1) : '-'}</div>
                  <div className="cellScore">{typeof directReportScore === 'number' && !Number.isNaN(directReportScore) ? Number(directReportScore).toFixed(1) : '-'}</div>
                  <div className="cellScore">{typeof peerScore === 'number' && !Number.isNaN(peerScore) ? Number(peerScore).toFixed(1) : '-'}</div>
                  <div className="cellScore">{typeof otherScore === 'number' && !Number.isNaN(otherScore) ? Number(otherScore).toFixed(1) : '-'}</div>
                  <div className="cellScore">{averageScore === 0 || Number.isNaN(averageScore) ? "-" : averageScore.toFixed(1)}</div>
                </div>
              </div>
            })}
          </div>
        </div>
        <div className="footer-section" style={{ position: "absolute", bottom: "4px", left: "0" }}>
          <p className='explain-para' style={{ fontSize: "9px", color: "#00577D", lineHeight: "13px", marginTop: "16px" }}>
            Observer ratings are grouped by relationship (Manager, Direct Report, Peer, Other), with each column showing the group’s average score. The final column displays the overall observer average; the Self score is excluded.
          </p>
          <div className='details-footer' style={{ background: "#EAF1F5", fontSize: "10px", margin: "0", padding: "4px 10px", paddingTop: "0", marginTop: "8px" }}>
            <ul className='details-list'>
              <li>S - Self</li>
              <li>M - Manager</li>
              <li>D - Direct Report</li>
              <li>P - Peer</li>
              <li>O - Other</li>
              <li>A - Average</li>
            </ul>
          </div>
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
};

export default QF_ItemOnePage360;
